import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer } from "@/motion/variants";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { TradingTrustStrip } from "@/components/TradingTrustStrip";
import { TradingPartnersTrustBar } from "@/components/TrustBar";
import { 
  Heart, 
  Sparkles, 
  Baby, 
  Brain, 
  ShoppingCart, 
  Globe,
  Building2,
  FileCheck,
  CheckCircle,
  Award,
  Users,
  Target
} from "lucide-react";

const Brands = () => {
  // Brand showcase data with highlights
  const brands = [
    {
      name: "MIVIDA",
      tagline: "Doctor-Led Consumer Health Platform",
      description: "A comprehensive health platform focused on evidence-based solutions for wellness and vitality across all life stages.",
      detailedDescription: "MIVIDA represents our commitment to evidence-based consumer health solutions. Led by medical professionals, the platform focuses on comprehensive wellness across multiple health domains.",
      focus: [
        "Anti-Aging",
        "Skin & Hair & Nail Repair",
        "Nutrition",
        "Kids Growth",
        "Neuroscience"
      ],
      icon: <Heart className="h-8 w-8" />,
      logo: "/brands/mivida.png",
      color: "blue",
      features: [
        "Medical-grade formulations",
        "Doctor-supervised product development",
        "Science-backed wellness solutions",
        "Comprehensive health ecosystem"
      ],
      highlights: [
        {
          icon: <Brain className="h-5 w-5" />,
          title: "Neuroscience Solutions",
          description: "Advanced cognitive health and brain wellness products"
        },
        {
          icon: <Sparkles className="h-5 w-5" />,
          title: "Anti-Aging & Beauty",
          description: "Science-backed formulations for skin, hair, and nail health"
        },
        {
          icon: <Baby className="h-5 w-5" />,
          title: "Kids Growth & Nutrition",
          description: "Specialized nutrition solutions for children's development"
        }
      ]
    },
    {
      name: "Salvadora",
      tagline: "Essential Products for Developing Markets",
      description: "Dedicated to providing high-quality food and essential products that meet the fundamental needs of developing markets.",
      detailedDescription: "Salvadora is dedicated to making essential food and consumer products accessible to developing markets, ensuring quality and affordability go hand in hand.",
      focus: [
        "Food Products",
        "First Need Products",
        "Affordable Quality",
        "Market Accessibility"
      ],
      icon: <ShoppingCart className="h-8 w-8" />,
      logo: "/brands/salvadora.png",
      color: "gold",
      features: [
        "Essential food staples",
        "Affordable pricing",
        "Quality assurance",
        "Market-focused distribution"
      ],
      highlights: [
        {
          icon: <ShoppingCart className="h-5 w-5" />,
          title: "Essential Food Products",
          description: "Staples and food items that meet basic nutritional needs"
        },
        {
          icon: <Target className="h-5 w-5" />,
          title: "First Need Products",
          description: "Essential consumer goods for daily living"
        },
        {
          icon: <Users className="h-5 w-5" />,
          title: "Market-Focused Approach",
          description: "Products designed specifically for developing market needs"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="Our Brands | Consumer Health & Essential Products | Padmora"
        description="Discover MIVIDA, our doctor-led consumer health platform, and Salvadora, focused on food and essential products for developing markets."
        image="/og-brands.jpg"
        canonical="https://padmora.com/brands"
        keywords={[
          "MIVIDA",
          "Salvadora",
          "consumer health platform",
          "doctor-led health",
          "anti-aging products",
          "nutrition products",
          "essential products",
          "developing markets",
          "health and wellness",
          "consumer brands"
        ]}
        aiOptimization={{
          aiKeywords: [
            "consumer health technology",
            "medical-grade wellness products",
            "developing market solutions",
            "essential goods distribution",
            "health platform"
          ],
          entities: [
            "MIVIDA",
            "Salvadora",
            "Padmora",
            "Consumer Health",
            "Essential Products",
            "Developing Markets"
          ],
          topics: [
            "Consumer Health Platforms",
            "Wellness Products",
            "Essential Goods Distribution",
            "Market Development",
            "Health and Nutrition"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Padmora Brands",
          "description": "Consumer health and essential products brands under Padmora",
          "brand": [
            {
              "@type": "Brand",
              "name": "MIVIDA",
              "description": "Doctor-Led Consumer Health Platform"
            },
            {
              "@type": "Brand",
              "name": "Salvadora",
              "description": "Essential Products for Developing Markets"
            }
          ]
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Our Brands", url: "https://padmora.com/brands" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              Our Brands
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              Building consumer-focused brands that deliver quality, innovation, and value across health, wellness, and essential products
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Send enquiry
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <Link to="/consumer-pack" className="text-slate-700 hover:text-slate-900">
                  View Consumer Pack →
                </Link>
              </Button>
            </motion.div>
          </motion.div>

                    <TradingTrustStrip
            showBanking={false}
            additionalBadges={[
              {
                icon: <Award className="h-4 w-4" />,
                label: "Quality-Focused Brands",
                tooltip: "Consumer brands built on quality, innovation, and market understanding",
              },
              {
                icon: <Globe className="h-4 w-4" />,
                label: "Global Market Reach",
                tooltip: "Brands designed for diverse markets and consumer needs",
              },
            ]}
          />

          {/* Partners at bottom of hero */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Brands Showcase Section - Merged Portfolio and Highlights */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4 text-slate-900"
            >
              Our Brand Portfolio
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Two distinct brands, each serving unique market needs with quality, innovation, and purpose
            </motion.p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {brands.map((brand, index) => (
                <motion.div
                  key={brand.name}
                  variants={fadeInUp}
                  className={`bg-white border border-border/50 p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow ${
                    brand.color === "blue" 
                      ? "bg-gradient-to-br from-white to-blue-50/30" 
                      : "bg-gradient-to-br from-white to-amber-50/30"
                  }`}
                >
                  {/* Brand Header */}
                  <div className="mb-8">
                    {/* Logo Section - Centered and Prominent */}
                    <div className="flex justify-center mb-6">
                      {brand.logo ? (
                        <div className={`w-48 h-48 flex items-center justify-center bg-white rounded-xl p-6 border-2 shadow-sm ${
                          brand.color === "blue" ? "border-blue-200" : "border-amber-200"
                        }`}>
                          <img 
                            src={brand.logo} 
                            alt={`${brand.name} logo`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className={`w-48 h-48 rounded-xl flex items-center justify-center ${
                          brand.color === "blue" ? "bg-blue-100" : "bg-amber-100"
                        }`}>
                          <div className={`${brand.color === "blue" ? "text-blue-600" : "text-amber-600"}`}>
                            {brand.icon}
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Brand Name and Tagline - Centered */}
                    <div className="text-center">
                      <h3 className="text-3xl font-bold text-slate-900 mb-2">
                        {brand.name}
                      </h3>
                      <p className="text-xl text-slate-600 font-medium">
                        {brand.tagline}
                      </p>
                    </div>
                  </div>

                  {/* Brand Description */}
                  <p className="text-slate-700 mb-4">
                    {brand.description}
                  </p>
                  {brand.detailedDescription && (
                    <p className="text-slate-600 text-sm mb-6">
                      {brand.detailedDescription}
                    </p>
                  )}

                  {/* Focus Areas */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">
                      Focus Areas
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {brand.focus.map((area, areaIndex) => (
                        <span
                          key={areaIndex}
                          className={`px-3 py-1 rounded-full text-sm font-medium border ${
                            brand.color === "blue" 
                              ? "bg-blue-50 text-blue-700 border-blue-200" 
                              : "bg-amber-50 text-amber-700 border-amber-200"
                          }`}
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Key Features */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">
                      Key Features
                    </h4>
                    <ul className="space-y-2">
                      {brand.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2">
                          <CheckCircle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                            brand.color === "blue" ? "text-blue-600" : "text-amber-600"
                          }`} />
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Brand Highlights */}
                  {brand.highlights && brand.highlights.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">
                        Highlights
                      </h4>
                      <div className="space-y-3">
                        {brand.highlights.map((highlight, highlightIndex) => (
                          <div key={highlightIndex} className="flex items-start gap-2">
                            <div className={`mt-0.5 flex-shrink-0 ${
                              brand.color === "blue" ? "text-blue-600" : "text-amber-600"
                            }`}>
                              {highlight.icon}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{highlight.title}</p>
                              <p className="text-sm text-slate-600">{highlight.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-slate-900 mb-6"
            >
              Interested in Our Brands?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Connect with us to learn more about MIVIDA, Salvadora, or explore partnership opportunities with our brand portfolio.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                variant="default"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Brands;

